import memoizeOne from 'memoize-one';
import React, { Component, Fragment } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { validateHistoricProductDataViewDate } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	Product,
	ProductUpdate,
	SiMaxPrice as ApiSiMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/SiMaxPrice';
import { ErrorCode } from '~/Api/User/ProductGroup/SiMaxPrice/Error';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { formatNumericHumanDate } from '~/DateTime';
import { parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import Calculation from '~/SiMaxPrice/Calculation/CalculationContainer';
import ParametersPanel, { Values as ReferenceValues } from '~/SiMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/SiMaxPrice/ProductsTable/ProductsTable';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceInValues, ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';

interface Props {
	header: React.ReactNode;
	group: UserProductGroup;
	siMaxPrice: ApiSiMaxPrice | null;
	referenceBasketVersion: ReferenceBasketVersion | null;
	pricedProducts: PricedProduct<Product>[];
	slovenianProducts: ProductRow[];
	referencedProducts: ProductRow[];
	otherEuEeaProducts: ProductRow[];
	otherEuropeProducts: ProductRow[];
	otherProducts: ProductRow[];
	validationResult: ValidationResult | null;
	isFetching: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	currency: string | null;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(producttId: string, tabName: string): void;
	onUpdate(
		group: UserProductGroup,
		referenceBasketVersion: string,
		productType: string,
		packageSize: number | null,
		dosageStrength: number | null,
		historicProductDataViewDate: Date | null,
		products: ProductUpdate[]
	): void;
	onSave(group: UserProductGroup): void;
	onCurrencyChange(currency: string | null): void;
	onDeleteItems(groupId: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
}

interface Values {
	reference: ReferenceValues;
	product: { [key: string]: ProductValues };
}

interface State {
	values: Values | null;
	lastSelectedProductId: string | null;
	lastSeenProductIdDetail: string | null;
	removeProduct: GeneralProduct | null;
	checkedRows: GeneralProduct[];
}

export default class SiMaxPrice extends Component<Props, State> {
	static defaultProps = {
		siMaxPrice: null,
		validationResult: null,
		detailProductId: null,
		detailProductTabName: 'info',
	};

	state: State = {
		values: null,
		lastSelectedProductId: null,
		lastSeenProductIdDetail: null,
		removeProduct: null,
		checkedRows: [],
	};

	componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	resolveValues(): Values {
		const {
			props: { siMaxPrice },
			state: { values },
		} = this;
		return this.createValues(siMaxPrice, values);
	}

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	createValues = memoizeOne((siMaxPrice: ApiSiMaxPrice | null, values: Values | null): Values => {
		const historicProductDataViewDate = siMaxPrice?.reference.historicProductDataViewDate ?? null;

		return {
			reference: values?.reference ?? {
				packageSize: {
					value: String(siMaxPrice?.reference.packageSize ?? ''),
					isValid: true,
				},
				basketVersion: siMaxPrice?.reference.referenceBasketVersion || '',
				dosageStrength: {
					value: String(siMaxPrice?.reference.dosageStrength ?? ''),
					isValid: true,
				},
				productType: siMaxPrice?.reference.referencedProductType || '',
				historicProductDataViewDate: {
					value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
					dateValue: historicProductDataViewDate,
					isValid: false,
				},
			},
			product:
				siMaxPrice?.products.reduce((products: { [key: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					const hasSelectedValidReferencePrice =
						currentValues !== undefined &&
						product.referencePrices.find(
							(referencePrice) => referencePrice.calculatedPrice.type === currentValues.referencePrice.value?.value
						) !== undefined;
					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

					products[product.product.id] = {
						referencePrice: hasSelectedValidReferencePrice
							? currentValues.referencePrice
							: {
									value:
										referencePrice !== undefined
											? {
													value: referencePrice.calculatedPrice.type,
													parentValue: null,
													prompt: null,
											  }
											: null,
									isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
							  },
						packageSize: currentValues?.packageSize ?? {
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
							isValid: true,
						},
						packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
							isValid: true,
						},
						dosageStrength: currentValues?.dosageStrength ?? {
							value: product.dosageStrengthForCalculation !== null ? String(product.dosageStrengthForCalculation) : '',
							isValid: true,
						},
						selected: currentValues?.selected ?? product.selected,
					};
					return products;
				}, {}) ?? {},
		};
	});

	processValidationResult(validationResult: ValidationResult | null) {
		if (!validationResult) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referencePackageSizeIsMissing,
			},
		};
		const productValues: { [key: string]: ProductValues } = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			const { productId } = error;
			const values = productId ? productValues[productId] : null;
			if (!values || !productId) {
				return;
			}
			if (error.code === ErrorCode.requiredProductSettingsMissing) {
				productValues[productId] = {
					...values,
					packageSize: { ...values.packageSize, isValid: false },
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.illegalPriceTypeSelected) {
				productValues[productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		const invokeSave =
			reference.basketVersion !== values.reference.basketVersion ||
			reference.productType === values.reference.productType;

		this.setState({ values: { ...values, reference } }, () => {
			this.update();
			if (invokeSave) {
				this.props.onSave(this.props.group);
			}
		});
	};

	handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceInValues | ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string,
		dosageStrength: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice !== referencePrice;

		const defaultReferencePrice = referencePrice || {};

		this.setState(
			{
				values: {
					reference: values.reference,
					product: {
						...values.product,
						[item.product.id]: {
							referencePrice: {
								value: {
									value: '',
									parentValue: null,
									prompt: null,
									...defaultReferencePrice,
								},
								isValid: Boolean(item.referencePrices.length === 0 || referencePrice !== null),
							},
							packageSize: { value: packageSize, isValid: false },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: false,
							},
							dosageStrength: { value: dosageStrength, isValid: false },
							selected: currentValue.selected,
						},
					},
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				values: { reference: values.reference, product: productValues },
				lastSelectedProductId: item.product.id,
			},
			this.update
		);
	};

	selectProductRange(
		productValues: { [key: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		const referenceDosageStrength = parsePackageSize(referenceValues.dosageStrength.value);
		referenceValues.dosageStrength.isValid = referenceDosageStrength.isValid;

		referenceValues.historicProductDataViewDate.isValid =
			referenceValues.historicProductDataViewDate.value === '' ||
			(referenceValues.historicProductDataViewDate.dateValue !== null &&
				validateHistoricProductDataViewDate(referenceValues.historicProductDataViewDate.dateValue));

		let hasValidationError =
			referencePackageSize.isValid === false ||
			referenceDosageStrength.isValid === false ||
			referenceValues.historicProductDataViewDate.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit, dosageStrength } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);
			const parsedDosageStrength = parsePackageSize(dosageStrength.value);

			hasValidationError =
				hasValidationError ||
				!referencePrice.isValid ||
				parsedPackageSize.isValid === false ||
				parsedPackageSizeForSingleUnit.isValid === false ||
				parsedDosageStrength.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: Boolean(parsedPackageSize.isValid),
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: Boolean(parsedPackageSizeForSingleUnit.isValid),
				},
				dosageStrength: {
					...values.dosageStrength,
					isValid: Boolean(parsedDosageStrength.isValid),
				},
			};

			products.push(
				new ProductUpdate(
					productId,
					referencePrice.value?.value || null,
					parsedPackageSize.value,
					parsedPackageSizeForSingleUnit.value,
					parsedDosageStrength.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(
				this.props.group,
				referenceValues.basketVersion,
				referenceValues.productType,
				referencePackageSize.value,
				referenceDosageStrength.value,
				referenceValues.historicProductDataViewDate.dateValue,
				products
			);
		}
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	render() {
		const {
			props: {
				header,
				siMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				slovenianProducts,
				referencedProducts,
				otherEuEeaProducts,
				otherEuropeProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;
		const referenceDosageStrength = parsePackageSize(values.reference.dosageStrength.value).value;

		if (siMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider id={detailProductId} siMaxPrice={siMaxPrice}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => onShowDetail(productId, detailProductTabName)}
										onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{siMaxPrice && referenceBasketVersion ? <Calculation siMaxPrice={siMaxPrice} /> : null}

					<ParametersPanel
						group={group}
						values={values.reference}
						onChange={this.referenceValuesChanged}
						onSave={() => onSave(group)}
					/>

					{pricedProducts.length === 0 ? <ProductInstructions /> : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Slovenian products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(siMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={slovenianProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<Heading level={2}>Reference basket products</Heading>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							showReferenceDataSource
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(siMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={referencedProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 && otherEuEeaProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>EU / EEA products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherEuEeaProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(siMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={otherEuEeaProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 && otherEuropeProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Other European products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherEuropeProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(siMaxPrice?.reference.historicProductDataViewDate)}
							lastSelectedProductId={lastSelectedProductId}
							products={otherEuropeProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div>
									<Heading level={2}>Other</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<ProductsTable
							disableSelection
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(siMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={otherProducts}
							productValues={values.product}
							referenceDosageStrength={referenceDosageStrength}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}

					{removeProduct ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => this.setState({ removeProduct: null })}
							onConfirm={() => onSave(group)}
							onSuccess={() => this.setState({ removeProduct: null })}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}
}
